import React, { useState } from 'react';
import PlacesAutocomplete from 'react-places-autocomplete';
import { TextField, InputAdornment, IconButton, Chip, Box } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { isEmpty } from '../../utils/lodash';
import { ILocation, LocationOption } from '../../types/global/helper';
import CheckboxGroup from '../CheckboxGroup/CheckboxGroup';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { SentryCapture } from '../../analytics/Sentry';
import { showToastMessage } from '../../utils/AppUtils';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { Spinner } from '@chakra-ui/react';
import { COMPANY_BRAND_ORANGE } from '../../constants/companyBrand';
import { BRAND_LUXE_PRIMARY, BRAND_OUTLINE_VARIANT, BRAND_SURFACE_CONTAINER_HIGHEST, brandModalOutlinedInputSx } from '../../pages/BrandDashboard/brandDashboardTokens';

type Loc = {
    address: string;
    description: string;
    coordinates: number[];
};
interface LocationAutocompleteProps {
    value: Loc | Loc[];
    locationOptions?: LocationOption[];
    onSelect: (address: string, coordinates?: number[], formattedAddress?: ILocation) => void;
    onSelectLocationOption: (address: LocationOption | null) => void;
    onDelete?: (index?: number) => void;
    placeholder?: string;
    label?: string;
    withCordinates?: boolean;
    fieldStyle?: {
        bgcolor: string;
        borderRadius: string;
        input: { color: string };
        marginTop: string;
        label: { color: string };
        '& .MuiOutlinedInput-notchedOutline': { borderColor: string };
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: string };
        '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: string };
    };
    searchOptions?: {
        bounds?: google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral | undefined;
        componentRestrictions?: google.maps.places.ComponentRestrictions | undefined;
        location?: google.maps.LatLng | google.maps.LatLngLiteral | undefined;
        offset?: number | string | undefined;
        radius?: number | string | undefined;
        sessionToken?: any;
        types?: string[] | undefined;
        locationRestriction?: google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral | undefined;
    };
    /** Company edit: black field, black suggestions, orange location chip */
    uniformPageBlack?: boolean;
    /** Brand dashboard modals — obsidian field, luxe gold chip, no harsh white outline */
    brandDashboard?: boolean;
}

const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
    value,
    onSelect,
    placeholder = 'Search Location ...',
    fieldStyle,
    onDelete,
    locationOptions,
    onSelectLocationOption,
    searchOptions,
    withCordinates,
    uniformPageBlack = false,
    brandDashboard = false,
}) => {
    const [text, setText] = useState('');
    const { themeStyle } = useThemeContext();

    return (
        <>
            <PlacesAutocomplete
                value={text}
                onChange={text => setText(text)}
                onError={(error, clearSuggestion) => {
                    showToastMessage(error, 'warning');
                    SentryCapture(error);
                    clearSuggestion();
                }}
                onSelect={address => {
                    let formattedAddress = '';
                    if (withCordinates) {
                        geocodeByAddress(address)
                            .then(results => {
                                formattedAddress = results[0].formatted_address;
                                return getLatLng(results[0]);
                            })
                            .then(latLng => {
                                const locationData = {
                                    type: 'Point',
                                    coordinates: [latLng.lng, latLng.lat],
                                    address: formattedAddress,
                                    description: formattedAddress,
                                    short_name: formattedAddress.split(',')[0],
                                };
                                onSelect(address, [latLng.lat, latLng.lng], locationData);
                            })
                            .catch(error => console.error('Error', error));
                    } else {
                        onSelect(address);
                    }
                    setText('');
                }}
                searchOptions={searchOptions}>
                {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                    <div style={{ position: 'relative' }}>
                        <TextField
                            fullWidth
                            margin={brandDashboard ? 'dense' : 'normal'}
                            {...getInputProps({
                                placeholder,
                            })}
                            sx={{
                                ...fieldStyle,
                                ...(brandDashboard
                                    ? {
                                          bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                          '& .MuiInputBase-input': {
                                              fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                              fontSize: 14,
                                              color: '#f2f2f6',
                                          },
                                          ...brandModalOutlinedInputSx,
                                      }
                                    : uniformPageBlack
                                    ? {
                                          bgcolor: '#000000',
                                          '& .MuiOutlinedInput-root': {
                                              backgroundColor: '#000000',
                                              '& fieldset': {
                                                  borderColor: 'rgba(255,255,255,0.22)',
                                              },
                                              '&:hover fieldset': {
                                                  borderColor: 'rgba(255,255,255,0.4)',
                                              },
                                              '&.Mui-focused fieldset': {
                                                  borderColor: COMPANY_BRAND_ORANGE,
                                              },
                                          },
                                          '& .MuiInputBase-input': {
                                              fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                              fontSize: 12,
                                              color: '#ffffff',
                                          },
                                      }
                                    : {
                                          bgcolor: theme[themeStyle].deep_grey_lighter,
                                          '& .MuiOutlinedInput-root': {
                                              '& fieldset': {
                                                  borderColor: theme[themeStyle].tag_white,
                                              },
                                              '&:hover fieldset': {
                                                  borderColor: theme[themeStyle].highlight,
                                              },
                                              '&.Mui-focused fieldset': {
                                                  borderColor: theme[themeStyle].highlight,
                                              },
                                          },
                                          '& .MuiInputBase-input': {
                                              fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                              fontSize: 12,
                                              color: theme[themeStyle].tag_white,
                                          },
                                      }),
                            }}
                            InputProps={{
                                endAdornment: text ? (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setText('')} edge="end">
                                            <ClearIcon sx={{ color: theme[themeStyle].tag_white }} />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                                style: {
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM, // Replace with FONT_ENUM if needed
                                    fontSize: 12,
                                    color: theme[themeStyle].tag_white,
                                    borderColor: theme[themeStyle].tag_white,
                                },
                            }}
                        />
                        {(loading || suggestions.length > 0) && (
                            <div
                                style={{
                                    position: 'absolute',
                                    zIndex: 10,
                                    backgroundColor: brandDashboard
                                        ? BRAND_SURFACE_CONTAINER_HIGHEST
                                        : uniformPageBlack
                                        ? '#000000'
                                        : theme[themeStyle].secondaryBg,
                                    border: brandDashboard
                                        ? `1px solid ${BRAND_OUTLINE_VARIANT}`
                                        : uniformPageBlack
                                        ? '1px solid rgba(255,255,255,0.12)'
                                        : undefined,
                                    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                                    borderRadius: '8px',
                                    marginTop: '4px',
                                    width: '100%',
                                }}>
                                {loading && (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}>
                                        <Spinner h={2} w={2} color={theme[themeStyle].tag_white} />
                                    </Box>
                                )}
                                {suggestions.map(suggestion => {
                                    const style = brandDashboard
                                        ? {
                                              backgroundColor: suggestion.active
                                                  ? 'rgba(255,255,255,0.06)'
                                                  : BRAND_SURFACE_CONTAINER_HIGHEST,
                                              cursor: 'pointer',
                                              padding: '10px',
                                              color: '#f2f2f6',
                                          }
                                        : uniformPageBlack
                                        ? {
                                              backgroundColor: suggestion.active
                                                  ? 'rgba(255,255,255,0.08)'
                                                  : '#000000',
                                              cursor: 'pointer',
                                              padding: '10px',
                                              color: '#ffffff',
                                          }
                                        : {
                                              backgroundColor: suggestion.active
                                                  ? theme[themeStyle].deep_grey
                                                  : theme[themeStyle].secondaryBg,
                                              cursor: 'pointer',
                                              padding: '10px',
                                              color: theme[themeStyle].tag_white,
                                          };
                                    return (
                                        <div
                                            {...getSuggestionItemProps(suggestion, {
                                                style,
                                            })}
                                            key={suggestion.placeId}>
                                            {suggestion.description}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </PlacesAutocomplete>
            {Array.isArray(value)
                ? value.map((value, index) => (
                      <Chip
                          key={index}
                          label={value.address}
                          variant="outlined"
                          onDelete={() => onDelete && onDelete(index)}
                          sx={
                              brandDashboard
                                  ? {
                                        mr: 0.5,
                                        mt: 1,
                                        color: '#5f4200',
                                        fontWeight: 700,
                                        border: 'none',
                                        backgroundColor: BRAND_LUXE_PRIMARY,
                                        '& .MuiChip-deleteIcon': { color: '#5f4200' },
                                    }
                                  : uniformPageBlack
                                  ? {
                                        mr: 0.5,
                                        mt: 1,
                                        color: COMPANY_BRAND_ORANGE,
                                        fontWeight: 700,
                                        borderColor: `${COMPANY_BRAND_ORANGE}4d`,
                                        backgroundColor: `${COMPANY_BRAND_ORANGE}14`,
                                        '& .MuiChip-deleteIcon': { color: COMPANY_BRAND_ORANGE },
                                    }
                                  : {
                                        color: theme[themeStyle].tag_white,
                                        '& .MuiChip-deleteIcon': { color: theme[themeStyle].tag_white },
                                    }
                          }
                      />
                  ))
                : !isEmpty(value) && (
                      <Chip
                          label={value.address}
                          sx={
                              brandDashboard
                                  ? {
                                        mr: 0.5,
                                        mt: 1,
                                        color: '#5f4200',
                                        fontWeight: 700,
                                        border: 'none',
                                        backgroundColor: BRAND_LUXE_PRIMARY,
                                        '& .MuiChip-deleteIcon': { color: '#5f4200' },
                                    }
                                  : uniformPageBlack
                                  ? {
                                        mr: 0.5,
                                        mt: 1,
                                        color: COMPANY_BRAND_ORANGE,
                                        fontWeight: 700,
                                        borderColor: `${COMPANY_BRAND_ORANGE}4d`,
                                        backgroundColor: `${COMPANY_BRAND_ORANGE}14`,
                                        '& .MuiChip-deleteIcon': { color: COMPANY_BRAND_ORANGE },
                                    }
                                  : {
                                        mr: 0.5,
                                        color: theme[themeStyle].tag_white,
                                        '& .MuiChip-deleteIcon': { color: theme[themeStyle].tag_white },
                                    }
                          }
                          variant="outlined"
                          onDelete={onDelete}
                      />
                  )}
            {locationOptions && isEmpty(value) && isEmpty(text) && (
                <CheckboxGroup options={locationOptions} onChange={value => onSelectLocationOption(value)} />
            )}
        </>
    );
};

export default LocationAutocomplete;
